import React from "react";
import { Link } from "react-router-dom";
import { Paths } from "../constants/UrlConstants";
import formatCurrencyAmount from "./../helpers/FormatCurrencyAmount";
import layoutStyle from "../assets/css/modules/shared/layout.css";

const topSectionFirstParagraph = (props) => (
  <React.Fragment>
    <p>
      The details here explain the payments for the below treatment(s) as part
      of your claim {props.selectedClaim.ClaimNumber} for{" "}
      {props.selectedClaim.ClaimCondition}.
    </p>
    <ul>
      {props.distinctTreatmentDescriptions.map((treatmentDescription) => (
        <li>{treatmentDescription}</li>
      ))}
    </ul>
  </React.Fragment>
);

const bottomSectionForShortFall = (props) => {
  return props.isNotCashBenefitWithShortfall ? (
    <p>
      You need to pay{" "}
      <strong>
        {formatCurrencyAmount(
          props.selectedInvoice.PaymentDetails.TotalMemberLiabilityAmount
        )}
      </strong>{" "}
      directly to <strong>{props.treatingProviderName}</strong> as there were
      charges that you weren't covered for. Please see the information below for
      how to pay and further detail on why you need to pay.
    </p>
  ) : null;
};

const PaymentDetails = {
  benefitFooter: (
    <React.Fragment>
      You can view your remaining benefit entitlements for this membership year
      on your{" "}
      <Link className="hol-link" to={Paths.Cover}>
        cover
      </Link>{" "}
      page.
    </React.Fragment>
  ),
  backButtonLabel: "Claim details",
  backButtonLabelForNotification: "back",
  topSectionHeader: <h1>Payment details</h1>,
  topSectionBodyWithoutShortfallForMember: (props) => (
    <div>
      {topSectionFirstParagraph(props)}
      <p>
        We've reimbursed you for the full amount of{" "}
        <strong>
          {formatCurrencyAmount(
            props.selectedInvoice.PaymentDetails.AmountPaid
          )}{" "}</strong>
       . All your treatment was covered by your plan. We've paid for it and you
        don't need to do anything.
      </p>
    </div>
  ),
  topSectionBodyWithoutShortfallForProvider: (props) => (
    <div>
      {topSectionFirstParagraph(props)}
      <p>
        We've paid the full amount of{" "}
        <strong>
          {formatCurrencyAmount(
            props.selectedInvoice.PaymentDetails.AmountPaid
          )}
        </strong>
        . All your treatment was covered by your plan. We've paid for it and you
        don't need to do anything.
      </p>
    </div>
  ),
  topSectionBodyWithShortfallForProvider: (props) => (
    <div>
      {topSectionFirstParagraph(props)}
      {props.providerInformationAvailable && bottomSectionForShortFall(props)}
    </div>
  ),
  topSectionBodyWithShortfallForProviderWithReimbursement: (props) => (
    <div>
      {topSectionFirstParagraph(props)}
      <p>
        We've reimbursed the provider{" "}
        {formatCurrencyAmount(props.selectedInvoice.PaymentDetails.AmountPaid)}
      </p>
      {props.providerInformationAvailable && bottomSectionForShortFall(props)}
    </div>
  ),
  topSectionBodyWithShortfallForMemberWithReimbursment: (props) => (
    <div>
      {topSectionFirstParagraph(props)}
      <p>
        We've reimbursed you{" "}
        {formatCurrencyAmount(props.selectedInvoice.PaymentDetails.AmountPaid)}
      </p>
      {props.providerInformationAvailable && bottomSectionForShortFall(props)}
    </div>
  ),
  topSectionBodyWithShortfallForMember: (props) => (
    <div>
      {topSectionFirstParagraph(props)}
      {props.providerInformationAvailable && bottomSectionForShortFall(props)}
    </div>
  ),
  summaryTableHeader: "How has my payment been calculated?",
  summaryRowLabel1: "Total cost of treatment",
  summaryRowLabel2: "Total amount we have paid",
  summaryRowLabel3: "Total amount payable by you",
  summaryRowLabel4: "Total amount not payable by you",
  summaryRow4TooltipContent:
    "This isn’t your responsibility to pay. Please see the information below for further details.",
  summaryRow3TooltipContent:
    "If you've already paid, you don't need to do anything but please be aware that this display will not be updated.",
  whatIfIhavePaidAlreadyTitle: <h2>What if I've already paid?</h2>,
  whatIfIhavePaidAlreadyBody: (
    <p>If you've already paid, you don't need to do anything but please be aware that this display will not be updated.</p>
  ),
  shortfallSummary: {
    whatToPayTopLabel: "What to pay",
    weHavePaidTopLabel: "What we've paid",
    whoToPayLabel: "Who to pay",
    accountNameLabel : "Account name",
    whoWeHavePaidTopLabel: "Who we've paid",
    providerReferenceTopLabel: "Provider reference",
    howToPayYourContributionTitle: "How to pay your contribution",
    howToPayYourContributionBody: (
      <p>
        You will need to pay your provider directly using the details below. For
        more information on when you need to pay by, please contact your
        provider.
      </p>
    ),
    howWePayProviderWithNoShortfallTitle: "How we pay the provider",
    howWePayProviderWithNoShortfallBody: (
      <p>
        We have sent payment directly to the provider. For some providers, this
        may be on an interval basis. If you have any questions,{" "}
        <Link
          className={"hol-link " + layoutStyle.anchorDecorationUnderline}
          to={Paths.ContactUs}
        >
          contact us
        </Link>
        .
      </p>
    ),
    howWePayYouTitle: "How we'll pay you",
    howWePayYouBody: (invoiceDate) => (
      <p>
        If you've given us your bank details, we'll refund you by bank transfer in the next 10 days. Otherwise, we'll send a cheque in the post.
      </p>
    )
  },
  howToPayShortfall: {
    bankTransferTitle: "By bank transfer",
    bankTransferDescription: (props) => (
      <React.Fragment>
        Please transfer{" "}
        <strong>
          {formatCurrencyAmount(props.totalMemberLiabilityAmount)}
        </strong>{" "}
        using the following details:
      </React.Fragment>
    ),
    AccountNumberLabel: "Account number",
    SortCodeLabel: "Sort code",
    phoneCreditCardTitle: "By phone/ credit card",
    accountNameLabel : "Account name",
    phoneCreditCardContent: (props) => (
      <div>
        <p>
          Please pay{" "}
          <strong>
            {formatCurrencyAmount(props.totalMemberLiabilityAmount)}
          </strong>{" "}
          by calling the number below:
        </p>
        <p>{props.payee.Telephone}</p>
      </div>
    ),
    byPostChequeTitle: "By post/ cheque",
    byPostChequeContent: (props) => (
      <div>
        <p>
          Please make your cheque out to '
          <strong>{props.treatingProviderName}</strong>' and write the invoice
          reference on the back. Then, send to:
        </p>
        <p>{props.payeeAddress}</p>
      </div>
    ),
    byPostChequeContentpayee: (props) => (
      <div>
        <p>
          Please make your cheque out to '
          <strong>{props.payee.Name}</strong>' and write the invoice
          reference on the back. Then, send to:
        </p>
        <p>{props.payeeAddress}</p>
      </div>
    ),
  },
  HelpSection: {
    HeadingLinkTo: Paths.Help,
    HeadingLinkText: "Help",
    MoreHelpLinkTo: Paths.Help,
    MoreHelpLinkText: "View more help"
  },
  HelpSet: {
    PaymentCheque: {
      Question: "Why does my payment need to be made by cheque?",
      Answer: (
        <p>
          All of our claims are paid by cheque to the member or patient. We hold
          agreements with some of our specialists and hospitals to pay them
          directly.
        </p>
      )
    },
    ContactProvider: {
      Question: "Do I need to contact my provider about this payment?",
      Answer: {
        Any: (
          <p>
            You only need to contact your provider when we've been unable to pay
            the claim in full, due to an excess or plan restrictions. Where
            possible, we'll provide you with the payment options and details of
            the hospital/provider so that you can pay them directly.
          </p>
        ),
        HOL: (
          <p>
            You only need to contact your provider when we've been unable to pay
            the claim in full, due to an excess or policy restrictions. Where
            possible, we'll provide you with the payment options and details of
            the hospital/provider so that you can pay them directly.
          </p>
        )
      }
    },
    HowLongPayment: {
      Question: "How long will it take my payment to arrive?",
      Answer: (
        <p>
          We try wherever possible to send you payment within 10 working days.
          If we need further information before we can pay the claim, we'll let
          you know.
        </p>
      )
    }
  },
  paymentBreakdownHeader: <h2>What happened</h2>,
  paymentBreakdownText: (
    <p>Below is a summary of your claim and what's been paid. We've shown where a payment is needed and once you've made a payment
      you can ignore this instruction.</p>
  ),
  whatYouHaveUsedSection: {
    Header: "What you've used",
    SubBenefitSectionDescription:
      "This is how this claim has affected your plan for this membership year.",
    SubBenefitSectionUsedPreviouslyText: "Used previously: ",
    SubBenefitSectionUsedThisTimeText: "Used this time: ",
    SubBenefitSectionRemainingText: "Remaining for you to use: ",
    SubBenefitSectionDoughnutTitleText: " total for this policy year: ",
    SubBenefitSectionNoLimitText: "No limit",
    ExcessSectionDescription:
      "This is a summary of your excess for this membership year. Once your excess has been paid, you won't need to pay anything as long as your claims are covered.",
    ExcessSectionDoughnutTitleText: (
      <React.Fragment>
        <strong>Excess</strong> total for this membership year:{" "}
      </React.Fragment>
    ),
    ExcessSectionAmountUsedAcrossOtherPayments:
      "Amount used across other payments: ",
    ExcessSectionUsedThisTime: "Used this time: ",
    ExcessSectionRemaining: "Remaining excess for this membership year: "
  },
  row: {
    paymentNeededLabel: "payment shortfalled",
    costCoveredLabel: "cost covered",
    costOfTreatment: "Cost of treatment",
    amountPaidLabel: "We paid",
    youNeedToPayLabel: "You need to pay",
    shortfallReasons: {
      ContractAdjustment: (
        <React.Fragment>
          The difference in the amount claimed by the provider and the amount we
          have paid is due to a contractual agreement we have with this
          provider. This isn’t your responsibility to pay and you don’t need to
          do anything. If the provider does contact you about this, please{" "}
          <Link
            className={"hol-link " + layoutStyle.anchorDecorationUnderline}
            to={Paths.ContactUs}
          >
            contact us
          </Link>{" "}
          and we will contact the provider directly.
        </React.Fragment>
      ),
      CustomerContribution: (
        <React.Fragment>
          There are costs from your treatment that your plan does not cover.
          You’ll need to settle this amount directly with the treatment
          provider. For details on how to do this, simply get in touch and we’ll
          be happy to help. Alternatively, you’ll find more information in your{" "}
          <Link
            className={"hol-link " + layoutStyle.anchorDecorationUnderline}
            to={Paths.CoverDocuments}
          >
            membership handbook
          </Link>
          .
        </React.Fragment>
      ),
      ExcessDeduction: (
        <React.Fragment>
          The excess you have on your plan has been applied to this amount and
          you’ll need to settle the outstanding amount directly with the
          provider. If you'd like more information please{" "}
          <Link
            className={"hol-link " + layoutStyle.anchorDecorationUnderline}
            to={Paths.ContactUs}
          >
            contact us
          </Link>{" "}
          or you can check your{" "}
          <Link
            className={"hol-link " + layoutStyle.anchorDecorationUnderline}
            to={Paths.CoverDocuments}
          >
            membership handbook
          </Link>
          .
        </React.Fragment>
      ),
      PolicyDeduction: (
        <React.Fragment>
          Your plan doesn’t cover these costs and you’ll need to settle the
          outstanding amount directly with the provider. If you’d like more
          information, please{" "}
          <Link
            className={"hol-link " + layoutStyle.anchorDecorationUnderline}
            to={Paths.ContactUs}
          >
            contact us
          </Link>{" "}
          or you can check your{" "}
          <Link
            className={"hol-link " + layoutStyle.anchorDecorationUnderline}
            to={Paths.CoverDocuments}
          >
            membership handbook
          </Link>
          .
        </React.Fragment>
      )
    }
  }
};

export default PaymentDetails;
